import React, { useMemo, useState, useEffect, useRef } from 'react';
import '../styles/ToolSelector.css';

/**
 * ToolSelector component
 * Props:
 * - availableTools: array of { id, name, slug, description }
 * - selectedIds: array of tool id strings
 * - onChange: function(newIds: string[])
 * - loading: boolean (optional)
 * - placeholder: string (optional)
 */
function ToolSelector({ availableTools = [], selectedIds = [], onChange, loading = false, disabled = false, placeholder = 'Type to search tools, press Enter to add' }) {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);

  // Build quick lookup
  const toolMap = useMemo(() => {
    const m = new Map();
    (availableTools || []).forEach(t => { if (t && t.id) m.set(t.id, t); });
    return m;
  }, [availableTools]);

  const normalizedSelected = useMemo(() => Array.from(new Set((selectedIds || []).filter(Boolean))), [selectedIds]);

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    const list = Array.isArray(availableTools) ? availableTools : [];
    // When there is no query and the input is active, show all items (scrollable via CSS)
    if (!q) return list;
    return list.filter(t => {
      const name = (t.name || '').toLowerCase();
      const slug = (t.slug || '').toLowerCase();
      return name.includes(q) || slug.includes(q) || (t.id || '').toLowerCase().includes(q);
    });
  }, [availableTools, query]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      const wrap = wrapperRef.current;
      if (!wrap) return;
      if (!wrap.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Recompute dropdown direction depending on viewport midpoint
  const recomputeDirection = () => {
    const wrap = wrapperRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
    const halfway = viewportH / 2;
    // If the top of the selector is below the halfway point, open upwards
    setOpenUp(rect.top > halfway);
  };

  useEffect(() => {
    if (!open) return;
    recomputeDirection();
    const onScroll = () => recomputeDirection();
    const onResize = () => recomputeDirection();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const addId = (id) => {
    if (disabled) return;
    const val = String(id || '').trim();
    if (!val) return;
    const next = Array.from(new Set([...(selectedIds || []), val]));
    onChange && onChange(next);
    setQuery('');
  };

  const removeId = (id) => {
    if (disabled) return;
    const next = (selectedIds || []).filter(x => x !== id);
    onChange && onChange(next);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      const current = filtered[highlight];
      if (current && current.id) {
        addId(current.id);
      } else if (query && query.trim()) {
        addId(query.trim()); // allow free-form id entry
      }
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      recomputeDirection();
      setHighlight(h => Math.min(h + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(0, h - 1));
    }
  };

  // Show dropdown whenever the input is active (focused/open), regardless of query
  const shouldShowDropdown = open;

  // Ensure highlighted item is visible within scroll box
  useEffect(() => {
    if (!shouldShowDropdown) return;
    const listEl = listRef.current;
    if (!listEl) return;
    const children = listEl.querySelectorAll('.dropdown-item');
    if (!children || children.length === 0) return;
    const idx = Math.max(0, Math.min(highlight, children.length - 1));
    const item = children[idx];
    if (item && typeof item.scrollIntoView === 'function') {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [highlight, shouldShowDropdown]);

  return (
    <div className={`tool-selector ${disabled ? 'is-disabled' : ''}`} ref={wrapperRef}>
      <div className="selected-chips">
        {normalizedSelected.length === 0 && (
          <div className="empty-note">No tools selected.</div>
        )}
        {normalizedSelected.map(id => {
          const tool = toolMap.get(id);
          const missing = !tool;
          const label = tool?.name || tool?.slug || id;
          return (
            <span className={`chip ${missing ? 'missing' : ''}`} key={id} title={missing ? 'Missing tool (not in the available list)' : (tool?.description || '')}>
              <i className={`fas ${missing ? 'fa-exclamation-triangle' : 'fa-wrench'}`}></i>
              <span className="chip-label">{label}</span>
              {missing && <span className="chip-sub">{id.slice(0, 8)}…</span>}
              <button type="button" className="chip-remove" onClick={() => removeId(id)} aria-label="Remove tool" disabled={disabled}>
                &times;
              </button>
            </span>
          );
        })}
      </div>

      <div className="tool-combobox">
        <input
          ref={inputRef}
          type="text"
          className="tool-input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); recomputeDirection(); }}
          onFocus={() => { if (!disabled) { setOpen(true); recomputeDirection(); } }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={loading || disabled}
          aria-autocomplete="list"
        />
        {shouldShowDropdown && (
          <div className={`dropdown ${openUp ? 'up' : 'down'}`} ref={listRef} data-direction={openUp ? 'up' : 'down'}>
            {loading ? (
              <div className="dropdown-item muted"><i className="fas fa-spinner fa-spin"></i> Loading tools…</div>
            ) : filtered.length === 0 && query ? (
              <div className="dropdown-item muted">Press Enter to add "{query}"</div>
            ) : (
              filtered.map((t, idx) => {
                const isSelected = (selectedIds || []).includes(t.id);
                return (
                  <button
                    type="button"
                    key={t.id}
                    className={`dropdown-item ${idx === highlight ? 'active' : ''} ${isSelected ? 'selected' : ''}`}
                    onMouseEnter={() => setHighlight(idx)}
                    onMouseDown={(e) => { e.preventDefault(); addId(t.id); setOpen(false); }}
                    title={t.description || ''}
                  >
                    <span className="title">{t.name || t.slug || t.id}</span>
                    {t.slug && <span className="slug">{t.slug}</span>}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ToolSelector;
